import { useState, useEffect, useCallback } from 'react';
import { activateUpdate } from '@/lib/pwa/updateLifecycle';

interface PWAState {
  isOnline: boolean;
  isInstallable: boolean;
  isInstalled: boolean;
  canUpdate: boolean;
  installPrompt: any;
}

interface PWAActions {
  install: () => Promise<void>;
  update: () => void;
  cacheMenu: (url: string) => void;
}

export const usePWA = (): PWAState & PWAActions => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [canUpdate, setCanUpdate] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<any>(null);
  const [serviceWorker, setServiceWorker] = useState<ServiceWorkerRegistration | null>(null);

  useEffect(() => {
    // Detectar estado de conexión
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Detectar si ya está instalado como PWA
    const checkIfInstalled = () => {
      const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches;
      const isInWebAppMode = (window.navigator as any).standalone === true;
      setIsInstalled(isInStandaloneMode || isInWebAppMode);
    };
    
    checkIfInstalled();

    // El registro se centraliza en main.tsx; aquí solo observamos su estado.
    const observeServiceWorker = async () => {
      if ('serviceWorker' in navigator) {
        try {
          const registration = await navigator.serviceWorker.ready;
          setServiceWorker(registration);

          // Caso 1: el trabajador nuevo ya estaba esperando antes de montar.
          if (registration.waiting && navigator.serviceWorker.controller) {
            setCanUpdate(true);
          }

          // Caso 2: se está instalando ahora mismo.
          const watch = (worker: ServiceWorker | null) => {
            if (!worker) return;
            worker.addEventListener('statechange', () => {
              if (worker.state === 'installed' && navigator.serviceWorker.controller) {
                setCanUpdate(true);
              }
            });
          };

          watch(registration.installing);
          registration.addEventListener('updatefound', () => watch(registration.installing));

          // Caso 3: el trabajador nuevo ya tomó el control → no hay nada pendiente.
          navigator.serviceWorker.addEventListener('controllerchange', () => {
            setCanUpdate(false);
          });
        } catch (error) {
          console.error('No se pudo observar el Service Worker:', error);
        }
      }
    };

    // Detectar prompt de instalación
    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event);
      setIsInstallable(true);
      console.log('📱 App lista para instalar');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    observeServiceWorker();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const install = async (): Promise<void> => {
    if (!installPrompt) return;
    
    try {
      const result = await installPrompt.prompt();
      console.log('📱 Resultado instalación:', result);
      
      if (result.outcome === 'accepted') {
        setIsInstalled(true);
        setIsInstallable(false);
      }
      
      setInstallPrompt(null);
    } catch (error) {
      console.error('❌ Error instalando PWA:', error);
    }
  };

  /**
   * Camino canónico de actualización: espera a que el trabajador nuevo tome el
   * control y recarga una sola vez (ver src/lib/pwa/updateLifecycle.ts).
   */
  const update = useCallback((): void => {
    setCanUpdate(false);
    void activateUpdate();
  }, []);

  const cacheMenu = (url: string): void => {
    if (serviceWorker) {
      serviceWorker.active?.postMessage({
        type: 'CACHE_MENU',
        url: url
      });
    }
  };

  return {
    isOnline,
    isInstallable,
    isInstalled,
    canUpdate,
    installPrompt,
    install,
    update,
    cacheMenu
  };
};